import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useApiSend } from '../../../utils/httpClientTs';
import { useQueryClient } from '@tanstack/react-query';
import {
  createIntroduction,
  updateIntroduction,
} from '../../../endpoints/introduction';
import {
  IntroductionCreateRequest,
  IntroductionForm as IntroductionFormType,
  IntroductionUpdateRequest,
} from '../../../types/introduction';
import CountrySelector from '../../../Components/CountrySelector';
import StateSelector from '../../../Components/StateSelector';
import AlertItem from '../../../Components/AlertItem';

interface IntroductionFormProps {
  initialData?: IntroductionFormType;
  onSuccess?: () => void;
  onCancel?: () => void;
  isEdit?: boolean;
}

const IntroductionForm: React.FC<IntroductionFormProps> = ({
  initialData,
  onSuccess,
  onCancel,
  isEdit = false,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<IntroductionCreateRequest>(() => {
    if (isEdit && initialData) {
      return {
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        email: initialData.email || '',
        phone_cell: initialData.phone_cell || '',
        phone_home: initialData.phone_home || '',
        country_id: initialData.country_id || '',
        state_id: initialData.state_id || null,
        postal_code: initialData.postal_code || '',
        address_1: initialData.address_1 || null,
        address_2: initialData.address_2 || null,
        city: initialData.city || null,
        description: initialData.description || '',
      };
    }
    return {
      first_name: '',
      last_name: '',
      email: '',
      phone_cell: '',
      phone_home: '',
      country_id: '',
      state_id: null,
      postal_code: '',
      address_1: null,
      address_2: null,
      city: null,
      description: '',
    };
  });
  const [errors, setErrors] = useState<Partial<IntroductionCreateRequest>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [postalCodeData, setPostalCodeData] = useState<any>(null);
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const queryClient = useQueryClient();

  const createIntroductionMutation = useApiSend(
    createIntroduction,
    (data) => {
      setApiError(null);
      // Invalidate the introductions list cache to refetch the data
      queryClient.invalidateQueries({ queryKey: ['introductions'] });
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/introductions');
      }
    },
    (error: any) => {
      handleApiError(error, 'Failed to create introduction. Please try again.');
    }
  );

  const updateIntroductionMutation = useApiSend(
    (params: { id: string; data: IntroductionUpdateRequest }) =>
      updateIntroduction(params.id, params.data),
    (data) => {
      setApiError(null);
      // Invalidate the introductions list cache to refetch the data
      queryClient.invalidateQueries({ queryKey: ['introductions'] });
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/introductions');
      }
    },
    (error: any) => {
      handleApiError(error, 'Failed to update introduction. Please try again.');
    }
  );

  const handleApiError = (error: any, defaultMessage: string) => {
    let errorMessage = defaultMessage;

    if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    setApiError(errorMessage);
  };

  // Postal code lookup effect
  useEffect(() => {
    if (formData.postal_code && selectedCountry?.name) {
      const lookupPostalCode = async () => {
        try {
          // Using a free postal code API - you can replace with your preferred service
          const response = await fetch(
            `https://api.zippopotam.us/${selectedCountry.name}/${formData.postal_code}`
          );
          if (response.ok) {
            const data = await response.json();
            setPostalCodeData(data);
          } else {
            setPostalCodeData(null);
          }
        } catch (error) {
          console.error('Error looking up postal code:', error);
          setPostalCodeData(null);
        }
      };

      const timeoutId = setTimeout(lookupPostalCode, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setPostalCodeData(null);
    }
  }, [formData.postal_code, selectedCountry]);

  const handleInputChange =
    (field: keyof IntroductionCreateRequest) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));

      // Clear errors when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }

      // Clear API error when user makes changes
      if (apiError) {
        setApiError(null);
      }
    };

  const handleCountryChange = (country: any) => {
    setSelectedCountry(country);
    setFormData((prev) => ({
      ...prev,
      country_id: country?.id || '',
      state_id: null, // Reset state when country changes
    }));

    // Clear API error when user makes changes
    if (apiError) {
      setApiError(null);
    }
  };

  const handleStateChange = (state: any) => {
    setFormData((prev) => ({
      ...prev,
      state_id: state?.id || null,
    }));

    // Clear API error when user makes changes
    if (apiError) {
      setApiError(null);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<IntroductionCreateRequest> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.country_id.trim()) {
      newErrors.country_id = 'Country is required';
    }
    if (!formData.postal_code.trim()) {
      newErrors.postal_code = 'Postal code is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (validateForm()) {
      if (isEdit && initialData?.id) {
        // Convert formData to update request format
        const updateData: IntroductionUpdateRequest = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone_cell: formData.phone_cell,
          phone_home: formData.phone_home,
          country_id: formData.country_id,
          state_id: formData.state_id,
          postal_code: formData.postal_code,
          address_1: formData.address_1,
          address_2: formData.address_2,
          city: formData.city,
          description: formData.description,
        };
        updateIntroductionMutation.mutate({
          id: initialData.id,
          data: updateData,
        });
      } else {
        createIntroductionMutation.mutate(formData);
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/introductions"
          variant="body2"
          sx={{ cursor: 'pointer' }}
        >
          Non-Member Intro
        </Link>
        <Typography variant="body2" color="text.secondary">
          {isEdit ? 'Edit Non-Member Intro' : 'Create Non-Member Intro'}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Edit Non-Member Intro' : 'Create Non-Member Intro'}
        </Typography>
      </Box>

      {/* Clarification Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Important:</strong> Please enter information about the woman
          you would like to introduce to P.E.O., not your own information.
        </Typography>
      </Alert>

      {/* Form */}
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                Woman Being Introduced - Personal Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Her First Name *"
                value={formData.first_name}
                onChange={handleInputChange('first_name')}
                error={!!errors.first_name}
                helperText={
                  errors.first_name ||
                  "Enter the first name of the woman you're introducing"
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Her Last Name *"
                value={formData.last_name}
                onChange={handleInputChange('last_name')}
                error={!!errors.last_name}
                helperText={
                  errors.last_name ||
                  "Enter the last name of the woman you're introducing"
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Her Email Address *"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                error={!!errors.email}
                helperText={
                  errors.email ||
                  "Enter the email address of the woman you're introducing"
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Her Cell Phone"
                value={formData.phone_cell}
                onChange={handleInputChange('phone_cell')}
                placeholder="Optional - her cell phone number"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Her Home Phone"
                value={formData.phone_home}
                onChange={handleInputChange('phone_home')}
                placeholder="Optional - her home phone number"
              />
            </Grid>

            {/* Location Information */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{ mb: 2, color: 'primary.main', mt: 2 }}
              >
                Woman Being Introduced - Location Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <CountrySelector
                value={formData.country_id}
                handleChange={handleCountryChange}
                error={!!errors.country_id}
                errorMessage={errors.country_id}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StateSelector
                value={formData.state_id}
                handleChange={handleStateChange}
                countryId={formData.country_id}
                error={!!errors.state_id}
                errorMessage={errors.state_id}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Her Postal Code *"
                value={formData.postal_code}
                onChange={handleInputChange('postal_code')}
                error={!!errors.postal_code}
                helperText={
                  errors.postal_code || 'Enter the postal code where she lives'
                }
                placeholder="Enter her postal code"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Her City"
                value={formData.city || ''}
                onChange={handleInputChange('city')}
                placeholder="Optional - the city where she lives"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Her Address Line 1"
                value={formData.address_1 || ''}
                onChange={handleInputChange('address_1')}
                placeholder="Optional - her street address"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Her Address Line 2"
                value={formData.address_2 || ''}
                onChange={handleInputChange('address_2')}
                placeholder="Optional - apartment, suite, etc."
              />
            </Grid>

            {/* Postal Code Lookup Results */}
            {postalCodeData && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Location Found:</strong>{' '}
                    {postalCodeData.places?.[0]?.['place name']},{' '}
                    {postalCodeData.places?.[0]?.['state']},{' '}
                    {postalCodeData.country}
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Description */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{ mb: 2, color: 'primary.main', mt: 2 }}
              >
                Tell Us About The Woman You're Introducing
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description of the Woman You're Introducing *"
                value={formData.description}
                onChange={handleInputChange('description')}
                error={!!errors.description}
                helperText={
                  errors.description ||
                  "Tell us about this woman and why you'd like to introduce her to P.E.O. chapters"
                }
                placeholder="Please provide some information about the woman you're introducing..."
              />
            </Grid>

            {/* API Error Display */}
            {apiError && (
              <Grid item xs={12}>
                <AlertItem
                  severity="error"
                  title="Error Creating Introduction"
                  description={apiError}
                  onClose={() => setApiError(null)}
                />
              </Grid>
            )}

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={onCancel || (() => navigate('/introductions'))}
                  disabled={createIntroductionMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    createIntroductionMutation.isPending ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={createIntroductionMutation.isPending}
                  sx={{
                    backgroundColor: '#00A859',
                    '&:hover': { backgroundColor: '#008a4a' },
                  }}
                >
                  {createIntroductionMutation.isPending ||
                  updateIntroductionMutation.isPending
                    ? isEdit
                      ? 'Updating...'
                      : 'Creating...'
                    : isEdit
                      ? 'Update Non-Member Intro'
                      : 'Create Non-Member Intro'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default IntroductionForm;
